import api from './api';
import {
  Workflow,
  WorkflowNode,
  WorkflowEdge,
  PaginatedResponse,
  WorkflowVersion,
  WorkflowVersionDetail,
  WorkflowVersionListResponse,
  ActiveAssignmentsCountResponse,
} from '../types';

export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  flowData?: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  };
  requiresAsset?: boolean; // Whether this workflow requires an asset when assigning
  assetType?: string | null; // Target asset type: company, fund, property, other
  expectedDurationDays?: number; // Expected time to complete in days
  eventCategory?: string | null; // Event type or 'specific_date'
  eventTiming?: string | null; // 'pre' or 'post'
  tagIds?: string[];
}

export interface UpdateWorkflowRequest {
  name?: string;
  description?: string;
  flowData?: {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
  };
  status?: 'draft' | 'published' | 'archived';
  requiresAsset?: boolean; // Whether this workflow requires an asset when assigning
  assetType?: string | null; // Target asset type: company, fund, property, other
  expectedDurationDays?: number; // Expected time to complete in days
  eventCategory?: string | null; // Event type or 'specific_date'
  eventTiming?: string | null; // 'pre' or 'post'
  tagIds?: string[];
  createNewVersion?: boolean; // If true, create a new version before updating
  versionLabel?: string; // Optional custom label for the new version
  versionNotes?: string; // Optional notes describing what changed
}

export interface WorkflowFilters {
  search?: string;
  dashboardId?: string;
  widgetType?: string;
  status?: 'draft' | 'published' | 'archived';
  tagId?: string;
}

// Slim workflow descriptor for pickers (quick-start). Workers see their
// manager's published workflows here without owner-only list access.
export interface WorkflowOption {
  id: string;
  name: string;
  description?: string | null;
  eventCategory?: string | null;
  requiresAsset: boolean;
  enforceStepOrder?: boolean;
}

export const workflowService = {
  getWorkflows: async (
    page = 1,
    pageSize = 10,
    filters?: WorkflowFilters,
  ): Promise<PaginatedResponse<Workflow>> => {
    const params: Record<string, unknown> = { page, page_size: pageSize };
    if (filters?.search) params.search = filters.search;
    if (filters?.dashboardId) params.dashboard_id = filters.dashboardId;
    if (filters?.widgetType) params.widget_type = filters.widgetType;
    if (filters?.status) params.workflow_status = filters.status;
    if (filters?.tagId) params.tag_id = filters.tagId;

    const response = await api.get<PaginatedResponse<Workflow>>('/workflows', {
      params,
    });
    return response.data;
  },

  getAvailableWorkflows: async (): Promise<WorkflowOption[]> => {
    const response = await api.get<WorkflowOption[]>('/workflows/available');
    return response.data;
  },

  getWorkflow: async (id: string): Promise<Workflow> => {
    const response = await api.get<Workflow>(`/workflows/${id}`);
    return response.data;
  },

  createWorkflow: async (data: CreateWorkflowRequest): Promise<Workflow> => {
    const response = await api.post<Workflow>('/workflows', data);
    return response.data;
  },

  updateWorkflow: async (id: string, data: UpdateWorkflowRequest): Promise<Workflow> => {
    const response = await api.put<Workflow>(`/workflows/${id}`, data);
    return response.data;
  },

  deleteWorkflow: async (id: string): Promise<void> => {
    await api.delete(`/workflows/${id}`);
  },

  publishWorkflow: async (id: string): Promise<Workflow> => {
    const response = await api.post<Workflow>(`/workflows/${id}/publish`);
    return response.data;
  },

  duplicateWorkflow: async (id: string, newName?: string): Promise<Workflow> => {
    const response = await api.post<Workflow>(
      `/workflows/${id}/duplicate`,
      newName ? { name: newName } : undefined,
    );
    return response.data;
  },

  // Version management methods

  /**
   * Get the count of active assignments for a workflow.
   * Used to determine if versioning prompt should be shown when editing.
   */
  getActiveAssignmentsCount: async (id: string): Promise<ActiveAssignmentsCountResponse> => {
    const response = await api.get<ActiveAssignmentsCountResponse>(
      `/workflows/${id}/active-assignments-count`,
    );
    return response.data;
  },

  /**
   * List all versions of a workflow.
   */
  getWorkflowVersions: async (workflowId: string): Promise<WorkflowVersionListResponse> => {
    const response = await api.get<WorkflowVersionListResponse>(
      `/workflows/${workflowId}/versions`,
    );
    return response.data;
  },

  /**
   * Get details of a specific workflow version including the snapshot data.
   */
  getWorkflowVersion: async (
    workflowId: string,
    versionId: string,
  ): Promise<WorkflowVersionDetail> => {
    const response = await api.get<WorkflowVersionDetail>(
      `/workflows/${workflowId}/versions/${versionId}`,
    );
    return response.data;
  },

  /**
   * Manually create a new version snapshot of the current workflow state.
   */
  createWorkflowVersion: async (
    workflowId: string,
    versionLabel?: string,
  ): Promise<WorkflowVersion> => {
    const response = await api.post<WorkflowVersion>(`/workflows/${workflowId}/versions`, null, {
      params: versionLabel ? { version_label: versionLabel } : undefined,
    });
    return response.data;
  },
};
